import { useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

import type { Without } from '@/types/generic';
import { colors } from './MainTheme';

// Box size assumed when no width/height is supplied. The historical `default`
// tick offsets (7/4) were hand-tuned for a 22px box even though <Checkmark>
// falls back to 18px, so 22 is what keeps those call sites pixel-identical.
const DEFAULT_SIZE = 22;
const parsePx = (value?: string): number => {
  const n = value ? parseFloat(value) : NaN;
  return Number.isFinite(n) ? n : DEFAULT_SIZE;
};
// The tick is a fixed 4×9 rotated element; its offsets scale linearly with the
// box so it stays centered at any size (fit through 22px→7/4 and 20px→6/3).
// Only the `modern` variant uses these — `default` keeps its fixed 7/4 offsets
// so the ~36 existing call sites (7 of which pass width/height) don't shift.
export const tickLeft = (width?: string): number => parsePx(width) / 2 - 4;
export const tickTop = (height?: string): number => parsePx(height) / 2 - 7;

/**
 * Visual style of the checkbox:
 * - `default` — the original look: mid-grey (#505050) border, the border is
 *   dropped when checked, content-box sizing and fixed tick offsets.
 * - `modern` — the design-system look (Figma 168-1130): light-grey (#BDBDBD)
 *   border that turns green and is *kept* when checked, so the box never changes
 *   size between states; border-box sizing and size-derived tick offsets.
 */
export type CheckboxVariant = 'default' | 'modern';

export type CheckboxProps = Without<JSX.IntrinsicElements['input'], 'onChange', 'ref'> & {
  checked?: boolean;
  onChange?: (checked: boolean, e?: any) => void;
  className?: string;
  checkmarkClassName?: string;
  width?: string;
  height?: string;
  variant?: CheckboxVariant;
};

export const CheckboxStyle = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;
export const Checkmark = styled.span<{
  width?: string;
  height?: string;
  variant?: CheckboxVariant;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${(props) => (props.$width ? props.$width : '18px')};
  height: ${(props) => (props.$height ? props.$height : '18px')};
  min-width: ${(props) => (props.$width ? props.$width : '18px')};
  min-height: ${(props) => (props.$height ? props.$height : '18px')};
  /* border-box only under modern: the default variant's declared width/height
     has always been the content box, and switching it would shrink every
     existing checkbox by the 2px border. */
  ${(props) => (props.$variant === 'modern' ? 'box-sizing: border-box;' : '')}
  border: 2px solid ${(props) => (props.$variant === 'modern' ? '#BDBDBD' : '#505050')};
  border-radius: 4px;
  &:after {
    content: "";
    position: absolute;
    display: none;
  }
  &:before {
    content: " ";
    position: absolute;
    top: -17px;
    left: -17px;
    bottom: -17px;
    right: -17px;
    opacity: 0;
  }
  &:hover {
    ${(props) => (props.$variant === 'modern' ? `
      border: 2px solid #BDBDBD;
      background-color: ${colors.lightestGray};
    ` : `
      border: 2px solid #505050;
      background-color: #dedede;
    `)}
  }
`;

export const CheckboxContainer = styled.div<{
  disabled?: boolean;
  variant?: CheckboxVariant;
  width?: string;
  height?: string;
}>`
  cursor: pointer;
  font-size: 22px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  & input:checked ~ .checkmark {
    background-color: ${colors.green};
    /* modern keeps a green border so the box size doesn't shift on check */
    border: ${(props) => (props.$variant === 'modern' ? `2px solid ${colors.green}` : 'none')};
  }
  & input:checked ~ .checkmark:after {
    display: block;
  }
  & .checkmark:after {
    left: ${(props) => (props.$variant === 'modern' ? tickLeft(props.$width) : 7)}px;
    top: ${(props) => (props.$variant === 'modern' ? tickTop(props.$height) : 4)}px;
    width: 4px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
  ${(props) => (props.$disabled && `
    & input:checked ~ .checkmark {
      background: ${colors.grayish};
      cursor: default;
    }
  `)}
`;

export const Checkbox =
  ({ checked, onChange, className, checkmarkClassName, width, height, variant = 'default', ...props }: CheckboxProps) => {
    const [isChecked, setChecked] = useState(false);
    const handleClick = (e) => {
      if (props.disabled) return;
      if (typeof checked !== 'boolean') {
        const newval = !isChecked;
        setChecked(newval);
        onChange?.(newval, e);
      } else {
        onChange?.(!checked, e);
      }
    };

    return (
      <CheckboxContainer
        onClick={handleClick}
        className={className}
        $disabled={props.disabled || false}
        $variant={variant}
        $width={width}
        $height={height}
      >
        <CheckboxStyle
          type="checkbox"
          checked={checked ?? isChecked}
          readOnly
          {...props} // eslint-disable-line react/jsx-props-no-spreading
        />
        <Checkmark
          className={classNames('checkmark', checkmarkClassName)}
          $width={width}
          $height={height}
          $variant={variant}
        />
      </CheckboxContainer>
    );
  };
